// src/pages/CrmDashboard.tsx — CRM Request Analytics Dashboard
//
// 4-tab dashboard: Overview | Active Requests | Resolved Requests | Complaints
// Data source: /api/crm/* (Oracle Service Cloud via dw_gold Fabric Warehouse)
// Requires governed access to dashboards:crm_requests.

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Spinner, SpinnerSize, Dropdown, IDropdownOption,
  mergeStyleSets, keyframes, MessageBar, MessageBarType,
} from '@fluentui/react';
import {
  BarChart, Bar, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell,
} from 'recharts';
import {
  CaretLeft, ArrowsClockwise, CheckCircle, XCircle,
  Warning, ChartBar, Clock, Info, X as PhosphorX,
} from '@phosphor-icons/react';
import { accessService } from '../services/accessService';

// ── Palette ──────────────────────────────────────────────────────────────────
const CRM_BLUE = '#1D4ED8';
const GREEN    = '#2e873d';
const AMBER    = '#D97706';
const RED      = '#D32F2F';
const GREY     = '#747474';

const BLUE_PALETTE = [
  '#1D4ED8','#2563EB','#3B82F6','#60A5FA','#93C5FD',
  '#1E40AF','#7DD3FC','#BFDBFE','#0369A1','#075985',
];

const DIRECTORATE_COLOURS: Record<string, string> = {
  'Places and Spaces':           '#1D4ED8',
  'Urban Living':                '#0891B2',
  'Customer and Transformation': '#7C3AED',
  'Community Services':          '#2e873d',
  'Office of the CEO':           '#D97706',
  'Unassigned':                  '#9CA3AF',
};
const DIR_PALETTE = ['#1D4ED8','#0891B2','#7C3AED','#2e873d','#D97706','#DC2626','#0369A1','#6B7280'];
function dirColour(name: string, idx: number): string {
  return DIRECTORATE_COLOURS[name] ?? DIR_PALETTE[idx % DIR_PALETTE.length];
}

const CHANNEL_COLOURS: Record<string, string> = {
  Email: '#2563EB', eForm: '#2e873d', Phone: '#D97706',
  Letter: '#7C3AED', 'Mobile App': '#0891B2', Counter: '#F97316',
  Internal: '#6B7280', Councillor: '#DC2626', 'CEO/Director': '#9333EA',
  'Social Media': '#EC4899', Ministerial: '#F97316', Chat: '#14B8A6', Unknown: '#9CA3AF',
};

// Ageing bucket colours — green→red urgency gradient
const BUCKET_COLOURS: Record<string, string> = {
  'Up to 5 days': '#22c55e',
  '6-10 days':    '#86efac',
  '11-20 days':   '#f59e0b',
  '21-50 days':   '#f97316',
  '51+ days':     '#ef4444',
};
const BUCKET_KEYS = [
  { key: 'upTo5',  label: 'Up to 5 days' },
  { key: 'd6to10',  label: '6-10 days'    },
  { key: 'd11to20', label: '11-20 days'   },
  { key: 'd21to50', label: '21-50 days'   },
  { key: 'd51plus', label: '51+ days'     },
];

// ── Tabs ──────────────────────────────────────────────────────────────────────
type TabKey = 'overview' | 'active' | 'resolved' | 'complaints';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview',   label: 'Overview'          },
  { key: 'active',     label: 'Active Requests'   },
  { key: 'resolved',   label: 'Resolved Requests' },
  { key: 'complaints', label: 'Complaints'         },
];

// ── Types ─────────────────────────────────────────────────────────────────────
interface FilterOptions {
  directorates:      string[];
  allDepartments:    string[];
  dirHierarchy:      Record<string, string[]>;
  serviceGroups:     string[];
  allServiceTypesL1: string[];
  allServiceTypesL2: string[];
  serviceHierarchy:  Record<string, Record<string, string[]>>;
  channels:          string[];
}

interface OverviewData {
  kpis: {
    totalRequests: number; openRequests: number; closedRequests: number;
    eformRequests: number; eformPct: number;
    slaCompliancePct: number | null; withinSLA: number; breachedSLA: number;
    complaintCount: number;
  };
  byChannel:      { channel: string; count: number }[];
  byMonth:        { label: string; year: number; month: number; count: number }[];
  byDirectorate:  { directorate: string; count: number }[];
  byServiceGroup: { serviceGroup: string; count: number }[];
  meta: { dateFrom: number; dateTo: number; generatedAt: string };
}

interface ActiveDetailRow {
  refNumber: string; subject: string;
  serviceGroup: string; serviceTypeL1: string; serviceTypeL2: string;
  department: string; directorate: string; channel: string;
  daysOpen: number; createdDate: string; slaState: string; slaStatus: string;
}
interface ActiveData {
  kpis: { totalActive: number; nearingBreach: number; overdue: number };
  ageing: {
    serviceGroup: string;
    upTo5: number; d6to10: number; d11to20: number; d21to50: number; d51plus: number;
    total: number;
  }[];
  byDepartment: { department: string; directorate: string; count: number }[];
  byMonth:   { label: string; year: number; month: number; count: number }[];
  byChannel: { channel: string; count: number }[];
  customerSegments: { label: string; count: number }[];
  detail: ActiveDetailRow[];
  meta: { generatedAt: string };
}

interface ResolvedDetailRow {
  refNumber: string; subject: string;
  serviceGroup: string; serviceTypeL1: string; serviceTypeL2: string;
  department: string; directorate: string;
  bizDaysToResolve: number; createdDate: string; closedDate: string; slaStatus: string;
}
interface ResolvedData {
  kpis: { totalResolved: number; avgBizDays: number | null; slaCompliancePct: number | null };
  byMonth: { label: string; year: number; month: number; count: number; avgBizDays: number }[];
  byServiceType: {
    serviceGroup: string; serviceTypeL1: string; serviceTypeL2: string;
    count: number; avgBizDays: number | null;
    minBizDays: number; maxBizDays: number; slaCompliancePct: number | null;
  }[];
  dayBuckets: { label: string; count: number }[];
  detail: ResolvedDetailRow[];
  meta: { dateFrom: number; dateTo: number; generatedAt: string };
}

interface ComplaintsData {
  kpis: { totalComplaints: number };
  byServiceGroup: { serviceGroup: string; count: number }[];
  byServiceType:  { serviceGroup: string; serviceTypeL1: string; count: number }[];
  byMonth: { label: string; year: number; month: number; count: number }[];
  detail: {
    refNumber: string; subject: string; status: string;
    serviceGroup: string; serviceTypeL1: string; serviceTypeL2: string;
    department: string; directorate: string;
    createdDate: string; closedDate: string; slaStatus: string;
  }[];
  meta: { dateFrom: number; dateTo: number; generatedAt: string };
}

// ── Date range helpers ────────────────────────────────────────────────────────
const DATE_RANGE_OPTIONS: IDropdownOption[] = [
  { key: '14d',  text: 'Last 14 days'   },
  { key: '30d',  text: 'Last 30 days'   },
  { key: '90d',  text: 'Last 3 months'  },
  { key: '180d', text: 'Last 6 months'  },
  { key: '365d', text: 'Last 12 months' },
  { key: 'all',  text: 'All time'       },
];

function buildDateKeys(preset: string): { from: number; to: number } {
  const today = new Date();
  const from  = new Date(today);
  const days: Record<string, number> = { '14d': 14, '30d': 30, '90d': 90, '180d': 180, '365d': 365 };
  if (preset in days) {
    from.setDate(today.getDate() - days[preset]);
  } else {
    from.setFullYear(2022, 6, 1);  // all-time: earliest Oracle CX data = July 2022
  }
  const fmt = (d: Date) => parseInt(d.toISOString().slice(0, 10).replace(/-/g, ''), 10);
  return { from: fmt(from), to: fmt(today) };
}

function slaColor(pct: number | null): string {
  if (pct === null) return GREY;
  if (pct >= 90) return GREEN;
  if (pct >= 85) return AMBER;
  return RED;
}
function slaLabel(pct: number | null): string {
  if (pct === null) return '—';
  if (pct >= 90) return 'On Target';
  if (pct >= 85) return 'Near Target';
  return 'Below Target';
}

function fmtNum(n: number): string {
  return n.toLocaleString('en-AU');
}
function trunc(s: string, max = 26): string {
  return s && s.length > max ? s.slice(0, max) + '…' : (s || '');
}

// ── Spin animation ────────────────────────────────────────────────────────────
const spinAnim = keyframes({ from: { transform: 'rotate(0deg)' }, to: { transform: 'rotate(360deg)' } });

// ── Styles ────────────────────────────────────────────────────────────────────
const s = mergeStyleSets({
  page: {
    padding: 32, minHeight: 'calc(100vh - 52px)',
    background: '#F5F5F5', fontFamily: "'Segoe UI', system-ui, sans-serif",
    '@media (max-width:768px)': { padding: 16 },
  } as any,
  container: { maxWidth: 1280, margin: '0 auto' },

  headerRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    marginBottom: 20, flexWrap: 'wrap' as const, gap: 8,
  },
  backBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    color: CRM_BLUE, fontWeight: 600, fontSize: 13, cursor: 'pointer',
    background: 'none', border: 'none', padding: 0, marginBottom: 4,
  },
  pageTitle: {
    fontSize: 26, fontWeight: 800, color: '#1A1A1A',
    letterSpacing: '-0.02em', display: 'flex', alignItems: 'center', gap: 10,
  },
  pageSub: { fontSize: 13, color: '#747474', marginTop: 2 },
  refreshBtn: {
    display: 'inline-flex', alignItems: 'center', gap: 6,
    padding: '7px 14px', background: '#fff', border: '1px solid #E1E1E1',
    borderRadius: 6, fontSize: 13, fontWeight: 600, color: '#484848',
    cursor: 'pointer', transition: 'all 0.15s',
    selectors: { ':hover': { borderColor: CRM_BLUE, color: CRM_BLUE } },
  } as any,

  filterBar: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 8,
    padding: '14px 20px', marginBottom: 0,
    display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap' as const, gap: 10,
  },
  filterGroup: { display: 'flex', flexDirection: 'column' as const, gap: 3 },
  filterLabel: {
    fontSize: 10, fontWeight: 700, color: '#9CA3AF',
    textTransform: 'uppercase' as const, letterSpacing: '0.07em',
  },
  clearBtn: {
    padding: '7px 14px', background: 'none', border: '1px solid #E1E1E1',
    borderRadius: 6, fontSize: 12, fontWeight: 600, color: '#747474',
    cursor: 'pointer', alignSelf: 'flex-end',
    selectors: { ':hover': { borderColor: RED, color: RED } },
  } as any,

  // Tab bar
  tabBar: {
    display: 'flex', borderBottom: '2px solid #E1E1E1',
    marginTop: 16, marginBottom: 24, gap: 0,
  },
  tabBtn: {
    padding: '10px 20px', fontSize: 14, fontWeight: 600,
    background: 'none', border: 'none', cursor: 'pointer', transition: 'all 0.15s',
    borderBottom: '2px solid transparent', marginBottom: -2,
  },

  // KPI row — adjusts grid per tab
  kpiRow3: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16, marginBottom: 24,
    '@media (max-width: 700px)': { gridTemplateColumns: '1fr' },
  } as any,
  kpiRow4: {
    display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24,
    '@media (max-width: 900px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
    '@media (max-width: 560px)': { gridTemplateColumns: '1fr' },
  } as any,
  kpiCard: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)',
  },
  kpiLabel: {
    fontSize: 10, fontWeight: 700, color: '#9CA3AF',
    textTransform: 'uppercase' as const, letterSpacing: '0.07em', marginBottom: 6,
  },
  kpiValue: { fontSize: 36, fontWeight: 800, letterSpacing: '-0.03em', lineHeight: '1', fontVariantNumeric: 'tabular-nums' },
  kpiSub: { fontSize: 12, color: '#9CA3AF', marginTop: 4 },
  kpiBadge: {
    display: 'inline-flex', alignItems: 'center', gap: 4,
    padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 700, marginTop: 6,
  },

  twoCol: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16,
    '@media (max-width: 860px)': { gridTemplateColumns: '1fr' },
  } as any,
  sectionCard: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16,
  },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: '#1A1A1A', marginBottom: 2 },
  sectionSub:   { fontSize: 12, color: '#9CA3AF', marginBottom: 14 },

  // Sortable table
  dataTable: {
    width: '100%', borderCollapse: 'collapse' as const, fontSize: 12,
  },
  th: {
    textAlign: 'left' as const, padding: '8px 10px', fontWeight: 700,
    fontSize: 11, color: '#747474', background: '#F9FAFB',
    borderBottom: '1px solid #E1E1E1', cursor: 'pointer',
    userSelect: 'none' as const, whiteSpace: 'nowrap' as const,
    selectors: { ':hover': { background: '#F3F4F6' } },
  } as any,
  td: {
    padding: '7px 10px', borderBottom: '1px solid #F3F4F6',
    color: '#232323', verticalAlign: 'top' as const,
  },

  centeredMsg: {
    display: 'flex', flexDirection: 'column' as const, alignItems: 'center',
    justifyContent: 'center', minHeight: 240, gap: 12,
    color: '#747474', fontSize: 14, textAlign: 'center' as const,
  },
  accessBox: {
    background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
    padding: 56, textAlign: 'center' as const, color: '#747474',
  },
  accessTitle: { fontWeight: 700, fontSize: 18, color: '#1A1A1A', marginBottom: 8 },
});

const tooltipStyle = {
  backgroundColor: '#fff', border: '1px solid #E1E1E1',
  borderRadius: 6, color: '#232323', fontSize: 12,
};

// ── Tooltip components ────────────────────────────────────────────────────────
function ChartTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ ...tooltipStyle, padding: '8px 12px' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.color || CRM_BLUE }}>
          {p.name}: {fmtNum(p.value)}
        </div>
      ))}
    </div>
  );
}
function ChannelTooltip({ active, payload, label, total }: any) {
  if (!active || !payload?.length) return null;
  const count = payload[0]?.value || 0;
  const pct   = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
  return (
    <div style={{ ...tooltipStyle, padding: '8px 12px' }}>
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div>{fmtNum(count)} requests ({pct}%)</div>
    </div>
  );
}
function AgeingTooltip({ active, payload, label }: any) {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s: number, p: any) => s + (p.value || 0), 0);
  return (
    <div style={{ ...tooltipStyle, padding: '8px 12px', minWidth: 160 }}>
      <div style={{ fontWeight: 700, marginBottom: 6 }}>{label}</div>
      {payload.map((p: any, i: number) => (
        <div key={i} style={{ color: p.fill, marginBottom: 2, display: 'flex', justifyContent: 'space-between', gap: 16 }}>
          <span>{p.name}</span>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>{fmtNum(p.value)}</span>
        </div>
      ))}
      <div style={{ borderTop: '1px solid #E1E1E1', marginTop: 4, paddingTop: 4, fontWeight: 700, display: 'flex', justifyContent: 'space-between' }}>
        <span>Total</span><span>{fmtNum(total)}</span>
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────
export default function CrmDashboard() {
  const nav = useNavigate();

  // Access gate
  const [hasAccess, setHasAccess] = useState<boolean | null>(null);

  // Active tab
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  // Shared filter state — multi-select arrays (empty = All)
  const [dateRange,    setDateRange]    = useState('90d');
  const [directorate,  setDirectorate]  = useState<string[]>([]);
  const [department,   setDepartment]   = useState<string[]>([]);
  const [serviceGroup, setServiceGroup] = useState<string[]>([]);
  const [svcL1,        setSvcL1]        = useState<string[]>([]);
  const [svcL2,        setSvcL2]        = useState<string[]>([]);
  const [channel,      setChannel]      = useState<string[]>([]);
  const [slaStatus,    setSlaStatus]    = useState('');   // Active tab SLA filter (single)

  // Filter options (loaded once on mount)
  const [filterOpts,       setFilterOpts]       = useState<FilterOptions | null>(null);
  const [filterOptsLoading, setFilterOptsLoading] = useState(false);

  // Per-tab data & state
  const [overviewData,   setOverviewData]   = useState<OverviewData | null>(null);
  const [activeData,     setActiveData]     = useState<ActiveData | null>(null);
  const [resolvedData,   setResolvedData]   = useState<ResolvedData | null>(null);
  const [complaintsData, setComplaintsData] = useState<ComplaintsData | null>(null);

  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  // ── Access check ────────────────────────────────────────────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{ role?: string; allowedDashboards?: string[] }>('/me');
        if (!alive) return;
        const d = res.data;
        if (d.role === 'admin') {
          setHasAccess(true);
        } else {
          setHasAccess(Array.isArray(d.allowedDashboards) && d.allowedDashboards.includes('crm_requests'));
        }
      } catch { setHasAccess(false); }
    })();
    return () => { alive = false; };
  }, []);

  // ── Load filter options (once after access confirmed) ────────────────────────
  useEffect(() => {
    if (!hasAccess) return;
    setFilterOptsLoading(true);
    accessService.get<FilterOptions>('/crm/filter-options')
      .then(r => setFilterOpts(r.data))
      .catch(e => console.warn('[CRM] filter-options failed:', e.message))
      .finally(() => setFilterOptsLoading(false));
  }, [hasAccess]);

  // ── Common filter params ─────────────────────────────────────────────────────
  const filterParams = useCallback(() => {
    const { from, to } = buildDateKeys(dateRange);
    return {
      dateFrom: from,
      dateTo:   to,
      // Arrays serialised as comma-separated strings; server splits on ','
      ...(directorate.length  ? { directorate:   directorate.join(',') }  : {}),
      ...(department.length   ? { department:    department.join(',') }   : {}),
      ...(serviceGroup.length ? { serviceGroup:  serviceGroup.join(',') } : {}),
      ...(svcL1.length        ? { serviceTypeL1: svcL1.join(',') }        : {}),
      ...(svcL2.length        ? { serviceTypeL2: svcL2.join(',') }        : {}),
      ...(channel.length      ? { channel:       channel.join(',') }      : {}),
      ...(slaStatus           ? { slaStatus }                             : {}),
    };
  }, [dateRange, directorate, department, serviceGroup, svcL1, svcL2, channel, slaStatus]);

  // ── Clear all tab data when filters change ───────────────────────────────────
  // The fetch effect below will re-load the active tab automatically because
  // filterParams (and therefore fetchCurrentTab) gets a new reference.
  useEffect(() => {
    setOverviewData(null);
    setActiveData(null);
    setResolvedData(null);
    setComplaintsData(null);
  }, [dateRange, directorate, department, serviceGroup, svcL1, svcL2, channel, slaStatus]);

  // ── Fetch helper (no dirty-gate — always loads when called) ─────────────────
  const fetchCurrentTab = useCallback(async (tab: TabKey) => {
    if (!hasAccess) return;
    setLoading(true);
    setError(null);
    try {
      const params = filterParams();
      if (tab === 'overview') {
        const r = await accessService.get<OverviewData>('/crm/overview', { params });
        setOverviewData(r.data);
      } else if (tab === 'active') {
        const r = await accessService.get<ActiveData>('/crm/active', { params });
        setActiveData(r.data);
      } else if (tab === 'resolved') {
        const r = await accessService.get<ResolvedData>('/crm/resolved', { params });
        setResolvedData(r.data);
      } else if (tab === 'complaints') {
        const r = await accessService.get<ComplaintsData>('/crm/complaints', { params });
        setComplaintsData(r.data);
      }
    } catch (e: any) {
      setError(e.response?.data?.error || e.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [hasAccess, filterParams]);

  // Fire whenever: tab switches OR filters change (filterParams ref changes → fetchCurrentTab ref changes)
  useEffect(() => {
    if (!hasAccess) return;
    fetchCurrentTab(activeTab);
  }, [activeTab, fetchCurrentTab, hasAccess]);

  // ── Cascade resets (multi-select) ───────────────────────────────────────────
  // directorate[] → remove departments no longer valid
  useEffect(() => {
    if (!directorate.length || !filterOpts) return;
    const allowed = new Set(directorate.flatMap(d => filterOpts.dirHierarchy[d] ?? []));
    setDepartment(prev => {
      const next = prev.filter(dep => allowed.has(dep));
      return next.length === prev.length ? prev : next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [directorate, filterOpts]);

  // serviceGroup[] → remove svcL1 values no longer valid
  useEffect(() => {
    if (!serviceGroup.length || !filterOpts) return;
    const allowed = new Set(serviceGroup.flatMap(sg => Object.keys(filterOpts.serviceHierarchy[sg] ?? {})));
    setSvcL1(prev => {
      const next = prev.filter(l1 => allowed.has(l1));
      return next.length === prev.length ? prev : next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceGroup, filterOpts]);

  // svcL1[] + serviceGroup[] → remove svcL2 values no longer valid
  useEffect(() => {
    if (!svcL1.length) { if (svcL2.length) setSvcL2([]); return; }
    if (!filterOpts) return;
    const allowed = new Set(svcL1.flatMap(l1 =>
      serviceGroup.flatMap(sg => filterOpts.serviceHierarchy[sg]?.[l1] ?? [])
    ));
    setSvcL2(prev => {
      const next = prev.filter(l2 => allowed.has(l2));
      return next.length === prev.length ? prev : next;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [svcL1, serviceGroup, filterOpts]);

  const clearFilters = () => {
    setDateRange('90d');
    setDirectorate([]); setDepartment([]);
    setServiceGroup([]); setSvcL1([]); setSvcL2([]);
    setChannel([]); setSlaStatus('');
  };
  const hasActiveFilter =
    directorate.length > 0 || department.length > 0 ||
    serviceGroup.length > 0 || svcL1.length > 0 || svcL2.length > 0 ||
    channel.length > 0 || !!slaStatus || dateRange !== '90d';

  // ── Derived filter option lists (cascade, multi-select aware) ───────────────
  const availableDepts: string[] = React.useMemo(() => {
    if (!directorate.length || !filterOpts?.dirHierarchy) return filterOpts?.allDepartments ?? [];
    return [...new Set(directorate.flatMap(d => filterOpts!.dirHierarchy[d] ?? []))].sort();
  }, [directorate, filterOpts]);

  const availableL1: string[] = React.useMemo(() => {
    if (!serviceGroup.length || !filterOpts?.serviceHierarchy) return filterOpts?.allServiceTypesL1 ?? [];
    return [...new Set(serviceGroup.flatMap(sg => Object.keys(filterOpts!.serviceHierarchy[sg] ?? {})))].sort();
  }, [serviceGroup, filterOpts]);

  const availableL2: string[] = React.useMemo(() => {
    if (!svcL1.length) return filterOpts?.allServiceTypesL2 ?? [];
    if (!filterOpts?.serviceHierarchy) return filterOpts?.allServiceTypesL2 ?? [];
    if (serviceGroup.length) {
      return [...new Set(svcL1.flatMap(l1 =>
        serviceGroup.flatMap(sg => filterOpts!.serviceHierarchy[sg]?.[l1] ?? [])
      ))].sort();
    }
    // No serviceGroup selected — show L2s that match any selected L1
    return filterOpts.allServiceTypesL2.filter(l2 =>
      Object.values(filterOpts.serviceHierarchy).some(sg =>
        Object.entries(sg).some(([l1k, l2s]) => svcL1.includes(l1k) && (l2s as string[]).includes(l2))
      )
    );
  }, [svcL1, serviceGroup, filterOpts]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const refreshedAt = (() => {
    const d = overviewData?.meta.generatedAt || activeData?.meta.generatedAt
      || resolvedData?.meta.generatedAt || complaintsData?.meta.generatedAt;
    return d ? new Date(d).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' }) : null;
  })();

  // ── Access gate ───────────────────────────────────────────────────────────────
  if (hasAccess === null) {
    return (
      <div className={s.page}>
        <div className={s.container} style={{ display: 'flex', justifyContent: 'center', paddingTop: 80 }}>
          <Spinner size={SpinnerSize.large} label="Checking access…" />
        </div>
      </div>
    );
  }
  if (hasAccess === false) {
    return (
      <div className={s.page}>
        <div className={s.container}>
          <button className={s.backBtn} onClick={() => nav('/dashboards')}>
            <CaretLeft size={12} weight="bold" /> Dashboards
          </button>
          <div className={s.accessBox}>
            <div className={s.accessTitle}>Access Required</div>
            <div style={{ fontSize: 14, color: '#747474', maxWidth: 440, margin: '0 auto' }}>
              The CRM Request Analytics dashboard requires governed access.
              Submit an access request to your administrator to get started.
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className={s.page}>
      <div className={s.container}>

        {/* Back nav */}
        <button className={s.backBtn} onClick={() => nav('/dashboards')}>
          <CaretLeft size={12} weight="bold" /> Dashboards
        </button>

        {/* Header */}
        <div className={s.headerRow}>
          <div>
            <div className={s.pageTitle}>
              <span style={{
                width: 36, height: 36, borderRadius: 8, background: CRM_BLUE,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>
                <ChartBar size={18} color="#fff" weight="bold" />
              </span>
              CRM Request Analytics
            </div>
            <div className={s.pageSub}>
              Oracle Service Cloud · {refreshedAt ? `Updated ${refreshedAt}` : 'Loading…'}
            </div>
          </div>
          <button
            className={s.refreshBtn}
            onClick={() => {
              if (activeTab === 'overview')   setOverviewData(null);
              if (activeTab === 'active')     setActiveData(null);
              if (activeTab === 'resolved')   setResolvedData(null);
              if (activeTab === 'complaints') setComplaintsData(null);
              fetchCurrentTab(activeTab);
            }}
            disabled={loading}
          >
            <ArrowsClockwise size={14} weight="bold"
              style={loading ? { animation: `${spinAnim} 1s linear infinite` } : {}} />
            Refresh
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <MessageBar messageBarType={MessageBarType.error}
            styles={{ root: { marginBottom: 12, borderRadius: 8 } }}>
            {error}
          </MessageBar>
        )}

        {/* Filter bar — two grouped rows */}
        <div style={{
          background: '#fff', border: '1px solid #E1E1E1', borderRadius: 8,
          padding: '14px 20px', marginBottom: 0,
          display: 'flex', flexDirection: 'column', gap: 12,
        }}>

          {/* ── Row 1: Period + Organisation ── */}
          <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10 }}>

            {/* Period */}
            <div className={s.filterGroup}>
              <span className={s.filterLabel}>
                {activeTab === 'resolved' ? 'Resolution date' : 'Period'}
              </span>
              <Dropdown options={DATE_RANGE_OPTIONS} selectedKey={dateRange}
                onChange={(_, o) => o && setDateRange(String(o.key))}
                styles={{ root: { minWidth: 148 }, dropdown: { fontSize: 13 } }} />
            </div>

            {/* Separator */}
            <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px', flexShrink: 0 }} />

            {/* Organisation group */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, color: '#C4C4C4',
                textTransform: 'uppercase', letterSpacing: '0.07em',
              }}>Organisation</span>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div className={s.filterGroup}>
                  <span className={s.filterLabel}>Directorate</span>
                  <Dropdown
                    multiSelect
                    placeholder="All directorates"
                    options={(filterOpts?.directorates ?? []).map(d => ({ key: d, text: d }))}
                    selectedKeys={directorate}
                    onChange={(_, o) => {
                      if (!o) return;
                      const v = String(o.key);
                      setDirectorate(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                    }}
                    disabled={filterOptsLoading || !filterOpts?.directorates.length}
                    styles={{ root: { minWidth: 185 }, dropdown: { fontSize: 13 }, callout: { minWidth: 300 } }} />
                </div>
                <div className={s.filterGroup}>
                  <span className={s.filterLabel} style={{ color: directorate.length ? '#9CA3AF' : '#D1D5DB' }}>
                    Department
                  </span>
                  <Dropdown
                    multiSelect
                    placeholder="All departments"
                    options={availableDepts.map(d => ({ key: d, text: d }))}
                    selectedKeys={department}
                    onChange={(_, o) => {
                      if (!o) return;
                      const v = String(o.key);
                      setDepartment(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                    }}
                    disabled={filterOptsLoading || !availableDepts.length}
                    styles={{ root: { minWidth: 185 }, dropdown: { fontSize: 13 }, callout: { minWidth: 340 } }} />
                </div>
              </div>
            </div>
          </div>

          {/* ── Row 2: Service + Channel + Clear ── */}
          <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10 }}>

            {/* Service group */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              <span style={{
                fontSize: 10, fontWeight: 700, color: '#C4C4C4',
                textTransform: 'uppercase', letterSpacing: '0.07em',
              }}>Service</span>
              <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div className={s.filterGroup}>
                  <span className={s.filterLabel}>Group</span>
                  <Dropdown
                    multiSelect
                    placeholder="All groups"
                    options={(filterOpts?.serviceGroups ?? []).map(g => ({ key: g, text: g }))}
                    selectedKeys={serviceGroup}
                    onChange={(_, o) => {
                      if (!o) return;
                      const v = String(o.key);
                      setServiceGroup(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                    }}
                    disabled={filterOptsLoading || !filterOpts?.serviceGroups.length}
                    styles={{ root: { minWidth: 185 }, dropdown: { fontSize: 13 }, callout: { minWidth: 320 } }} />
                </div>
                <div className={s.filterGroup}>
                  <span className={s.filterLabel} style={{ color: serviceGroup.length ? '#9CA3AF' : '#D1D5DB' }}>Type</span>
                  <Dropdown
                    multiSelect
                    placeholder="All types"
                    options={availableL1.map(l => ({ key: l, text: l }))}
                    selectedKeys={svcL1}
                    onChange={(_, o) => {
                      if (!o) return;
                      const v = String(o.key);
                      setSvcL1(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                    }}
                    disabled={filterOptsLoading || !availableL1.length}
                    styles={{ root: { minWidth: 185 }, dropdown: { fontSize: 13 }, callout: { minWidth: 300 } }} />
                </div>
                <div className={s.filterGroup}>
                  <span className={s.filterLabel} style={{ color: svcL1.length ? '#9CA3AF' : '#D1D5DB' }}>Sub-type</span>
                  <Dropdown
                    multiSelect
                    placeholder="All sub-types"
                    options={availableL2.map(l => ({ key: l, text: l }))}
                    selectedKeys={svcL2}
                    onChange={(_, o) => {
                      if (!o) return;
                      const v = String(o.key);
                      setSvcL2(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                    }}
                    disabled={filterOptsLoading || !availableL2.length || !svcL1.length}
                    styles={{ root: { minWidth: 185 }, dropdown: { fontSize: 13 }, callout: { minWidth: 320 } }} />
                </div>
              </div>
            </div>

            {/* Separator */}
            <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px', flexShrink: 0 }} />

            {/* Channel */}
            <div className={s.filterGroup}>
              <span className={s.filterLabel}>Channel</span>
              <Dropdown
                multiSelect
                placeholder="All channels"
                options={(filterOpts?.channels ?? []).map(c => ({ key: c, text: c }))}
                selectedKeys={channel}
                onChange={(_, o) => {
                  if (!o) return;
                  const v = String(o.key);
                  setChannel(prev => o.selected ? [...prev, v] : prev.filter(x => x !== v));
                }}
                disabled={filterOptsLoading || !filterOpts?.channels.length}
                styles={{ root: { minWidth: 155 }, dropdown: { fontSize: 13 }, callout: { minWidth: 240 } }} />
            </div>

            {/* SLA Status — only relevant for Active tab (single-select: Nearing vs Overdue are mutually exclusive) */}
            {activeTab === 'active' && (
              <>
                <div style={{ width: 1, background: '#E1E1E1', alignSelf: 'stretch', margin: '0 6px', flexShrink: 0 }} />
                <div className={s.filterGroup}>
                  <span className={s.filterLabel}>SLA Status</span>
                  <Dropdown
                    options={[
                      { key: '',        text: 'All requests'     },
                      { key: 'Nearing', text: 'Nearing breach'   },
                      { key: 'Overdue', text: 'Overdue only'     },
                    ]}
                    selectedKey={slaStatus}
                    onChange={(_, o) => o && setSlaStatus(o.key === '' ? '' : String(o.key))}
                    styles={{ root: { minWidth: 148 }, dropdown: { fontSize: 13 } }} />
                </div>
              </>
            )}

            {hasActiveFilter && (
              <button className={s.clearBtn} onClick={clearFilters} style={{ alignSelf: 'flex-end' }}>
                Clear filters
              </button>
            )}
          </div>

        </div>

        {/* Tab bar */}
        <div className={s.tabBar}>
          {TABS.map(tab => (
            <button
              key={tab.key}
              className={s.tabBtn}
              onClick={() => setActiveTab(tab.key)}
              style={{
                color: activeTab === tab.key ? CRM_BLUE : '#747474',
                borderBottom: activeTab === tab.key ? `2px solid ${CRM_BLUE}` : '2px solid transparent',
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Excluded-data note — applies to all tabs */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 7,
          marginTop: 12, marginBottom: 12,
          fontSize: 12, color: '#6B7280',
          background: '#F9FAFB', border: '1px solid #E5E7EB',
          borderRadius: 6, padding: '8px 12px',
        }}>
          <Info size={14} color="#6B7280" />
          Sensitive requests (no SLA assigned) and Cancelled requests are excluded from all dashboard figures.
        </div>

        {/* Loading state */}
        {loading && (
          <div className={s.centeredMsg}>
            <Spinner size={SpinnerSize.large} />
            <span>Loading {TABS.find(t => t.key === activeTab)?.label}…</span>
          </div>
        )}

        {/* Empty/error fallback — shown when not loading and tab data is still null */}
        {!loading && activeTab === 'active'     && !activeData     && !error && (
          <div className={s.centeredMsg} style={{ minHeight: 200 }}>
            <ArrowsClockwise size={24} color="#9CA3AF" />
            <span>No data loaded — click <strong>Refresh</strong> to load Active Requests.</span>
          </div>
        )}
        {!loading && activeTab === 'resolved'   && !resolvedData   && !error && (
          <div className={s.centeredMsg} style={{ minHeight: 200 }}>
            <ArrowsClockwise size={24} color="#9CA3AF" />
            <span>No data loaded — click <strong>Refresh</strong> to load Resolved Requests.</span>
          </div>
        )}
        {!loading && activeTab === 'complaints' && !complaintsData && !error && (
          <div className={s.centeredMsg} style={{ minHeight: 200 }}>
            <ArrowsClockwise size={24} color="#9CA3AF" />
            <span>No data loaded — click <strong>Refresh</strong> to load Complaints.</span>
          </div>
        )}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 1: OVERVIEW
           ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'overview' && !loading && overviewData && (() => {
          const { kpis, byChannel, byMonth, byDirectorate, byServiceGroup } = overviewData;
          const total   = kpis.totalRequests;
          const slaPct: number | null = kpis.slaCompliancePct;
          const slaClr  = slaColor(slaPct);
          const slaLbl  = slaLabel(slaPct);
          return (
            <>
              {/* KPIs */}
              <div className={s.kpiRow4}>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Total Requests</div>
                  <div className={s.kpiValue} style={{ color: CRM_BLUE }}>{fmtNum(total)}</div>
                  <div className={s.kpiSub}>active &amp; closed in period</div>
                </div>

                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Open Requests</div>
                  <div className={s.kpiValue}
                    style={{ color: total > 0 && kpis.openRequests / total > 0.3 ? AMBER : '#1A1A1A' }}>
                    {fmtNum(kpis.openRequests)}
                  </div>
                  <div className={s.kpiSub}>
                    {total > 0 ? `${((kpis.openRequests / total) * 100).toFixed(1)}% of total` : 'currently unresolved'}
                  </div>
                </div>

                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>eForm Requests</div>
                  <div className={s.kpiValue}>{fmtNum(kpis.eformRequests)}</div>
                  <span className={s.kpiBadge}
                    style={{ background: '#F0F7F1', color: GREEN, border: '1px solid #C3DFC7' }}>
                    {kpis.eformPct.toFixed(1)}% of total
                  </span>
                </div>

                <div className={s.kpiCard} style={{ borderLeft: `3px solid ${slaClr}` }}>
                  <div className={s.kpiLabel}>SLA Compliance</div>
                  {slaPct !== null ? (
                    <>
                      <div className={s.kpiValue} style={{ color: slaClr }}>
                        {slaPct.toFixed(1)}<span style={{ fontSize: 20, fontWeight: 600, marginLeft: 2 }}>%</span>
                      </div>
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                        {slaPct >= 90
                          ? <CheckCircle size={13} color={GREEN} weight="fill" />
                          : slaPct >= 85
                            ? <Warning size={13} color={AMBER} weight="fill" />
                            : <XCircle size={13} color={RED} weight="fill" />}
                        <span style={{ fontSize: 12, color: slaClr, fontWeight: 600 }}>{slaLbl}</span>
                        <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 2 }}>Target: 90%</span>
                      </div>
                    </>
                  ) : <div className={s.kpiValue} style={{ color: GREY }}>—</div>}
                  <div className={s.kpiSub}>
                    {slaPct !== null
                      ? `${fmtNum(kpis.withinSLA)} within · ${fmtNum(kpis.breachedSLA)} breached`
                      : 'No closed requests in period'}
                  </div>
                </div>
              </div>

              {/* Charts row 1: Channel + Monthly trend */}
              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Requests by Channel</div>
                  <div className={s.sectionSub}>{byChannel.length} channels · {fmtNum(total)} total</div>
                  <ResponsiveContainer width="100%" height={Math.max(180, byChannel.length * 36)}>
                    <BarChart data={byChannel} layout="vertical"
                      margin={{ top: 0, right: 40, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <YAxis type="category" dataKey="channel" width={92} tick={{ fontSize: 12 }}
                        tickFormatter={v => trunc(v, 14)} />
                      <Tooltip content={<ChannelTooltip total={total} />} />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={22}>
                        {byChannel.map((e, i) => (
                          <Cell key={i} fill={CHANNEL_COLOURS[e.channel] ?? BLUE_PALETTE[i % BLUE_PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Requests Over Time</div>
                  <div className={s.sectionSub}>Monthly volume · creation date</div>
                  {byMonth.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No monthly data for this range</div>
                    : (
                      <ResponsiveContainer width="100%" height={240}>
                        <AreaChart data={byMonth} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="crmGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor={CRM_BLUE} stopOpacity={0.18} />
                              <stop offset="95%" stopColor={CRM_BLUE} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd"
                            />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                          <Tooltip content={<ChartTooltip />} />
                          <Area type="monotone" dataKey="count" name="Requests" stroke={CRM_BLUE}
                            strokeWidth={2} fill="url(#crmGrad)"
                            dot={byMonth.length <= 24} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                </div>
              </div>

              {/* By Directorate */}
              {byDirectorate.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Requests by Directorate</div>
                  <div className={s.sectionSub}>Top-level organisational hierarchy · filtered period</div>
                  <ResponsiveContainer width="100%" height={Math.max(140, byDirectorate.length * 38)}>
                    <BarChart data={byDirectorate} layout="vertical"
                      margin={{ top: 0, right: 60, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <YAxis type="category" dataKey="directorate" width={210}
                        tick={{ fontSize: 12 }} tickFormatter={v => trunc(v, 30)} />
                      <Tooltip formatter={(v: any) => [fmtNum(v), 'Requests']}
                        labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={26}>
                        {byDirectorate.map((e, i) => (
                          <Cell key={i} fill={dirColour(e.directorate, i)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* By Service Group */}
              {byServiceGroup.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Requests by Service Group</div>
                  <div className={s.sectionSub}>Top service groups · filtered period</div>
                  <ResponsiveContainer width="100%" height={Math.max(180, byServiceGroup.length * 34)}>
                    <BarChart data={byServiceGroup} layout="vertical"
                      margin={{ top: 0, right: 60, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <YAxis type="category" dataKey="serviceGroup" width={200}
                        tick={{ fontSize: 11 }} tickFormatter={v => trunc(v, 28)} />
                      <Tooltip formatter={(v: any) => [fmtNum(v), 'Requests']}
                        labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={18}>
                        {byServiceGroup.map((_, i) => (
                          <Cell key={i} fill={BLUE_PALETTE[i % BLUE_PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Complaint callout */}
              {kpis.complaintCount > 0 && (
                <div style={{
                  background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8,
                  padding: '12px 18px', display: 'flex', alignItems: 'center', gap: 10,
                  fontSize: 13, color: '#991B1B', marginBottom: 16,
                }}>
                  <Warning size={16} weight="fill" color="#DC2626" />
                  <span>
                    <strong>{fmtNum(kpis.complaintCount)}</strong> complaint
                    {kpis.complaintCount !== 1 ? 's' : ''} in the selected period
                    — view the <button onClick={() => setActiveTab('complaints')}
                      style={{ background: 'none', border: 'none', color: '#DC2626', fontWeight: 700, cursor: 'pointer', textDecoration: 'underline', padding: 0 }}>
                      Complaints tab
                    </button> for details
                  </span>
                </div>
              )}
            </>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 2: ACTIVE REQUESTS
           ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'active' && !loading && activeData && (() => {
          const { kpis, ageing, byDepartment, byMonth, byChannel, customerSegments, detail: activeDetail } = activeData;
          const totalCusts = customerSegments.reduce((sum, x) => sum + x.count, 0);
          const segColours = ['#1D4ED8', '#22c55e', '#f59e0b', '#ef4444'];
          return (
            <>
              {/* KPIs */}
              <div className={s.kpiRow3}>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Total Active Requests</div>
                  <div className={s.kpiValue} style={{ color: CRM_BLUE }}>
                    {fmtNum(kpis.totalActive)}
                  </div>
                  <div className={s.kpiSub}>currently open (all dates)</div>
                </div>

                <div className={s.kpiCard} style={{ borderLeft: `3px solid ${AMBER}` }}>
                  <div className={s.kpiLabel}>Nearing SLA Breach</div>
                  <div className={s.kpiValue} style={{ color: kpis.nearingBreach > 0 ? AMBER : '#1A1A1A' }}>
                    {fmtNum(kpis.nearingBreach)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6 }}>
                    <Clock size={13} color={AMBER} weight="fill" />
                    <span style={{ fontSize: 12, color: AMBER, fontWeight: 600 }}>Due within 48 hours</span>
                  </div>
                </div>

                <div className={s.kpiCard} style={{ borderLeft: `3px solid ${RED}` }}>
                  <div className={s.kpiLabel}>SLA Overdue</div>
                  <div className={s.kpiValue} style={{ color: kpis.overdue > 0 ? RED : GREEN }}>
                    {fmtNum(kpis.overdue)}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6 }}>
                    {kpis.overdue > 0
                      ? <XCircle size={13} color={RED} weight="fill" />
                      : <CheckCircle size={13} color={GREEN} weight="fill" />}
                    <span style={{ fontSize: 12, color: kpis.overdue > 0 ? RED : GREEN, fontWeight: 600 }}>
                      {kpis.overdue > 0 ? 'Past SLA deadline' : 'No overdue requests'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Customer segmentation — how many customers have 1 / 2-5 / 6-10 / 11+ open requests */}
              {customerSegments.length > 0 && (
                <div className={s.sectionCard} style={{ marginBottom: 16 }}>
                  <div className={s.sectionTitle}>Customers by Request Volume</div>
                  <div className={s.sectionSub}>
                    {fmtNum(totalCusts)} customers with linked open requests — distribution by request count
                  </div>
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
                    gap: 12, marginTop: 10,
                  }}>
                    {customerSegments.map((seg, i) => {
                      const pct = totalCusts > 0 ? (seg.count / totalCusts) * 100 : 0;
                      const clr = segColours[i] ?? '#9CA3AF';
                      return (
                        <div key={i} style={{
                          textAlign: 'center', padding: '14px 10px',
                          border: '1px solid #E1E1E1', borderRadius: 8,
                          borderTop: `3px solid ${clr}`,
                        }}>
                          <div style={{
                            fontSize: 28, fontWeight: 800, color: clr,
                            fontVariantNumeric: 'tabular-nums', lineHeight: '1',
                          }}>
                            {pct.toFixed(1)}<span style={{ fontSize: 16, fontWeight: 600 }}>%</span>
                          </div>
                          <div style={{ fontSize: 12, fontWeight: 700, color: '#484848', marginTop: 5 }}>
                            {seg.label}
                          </div>
                          <div style={{ fontSize: 11, color: '#9CA3AF', marginTop: 2 }}>
                            {fmtNum(seg.count)} customers
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Trend by month + Channel breakdown (date-filtered) */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
                gap: 16, marginBottom: 16,
              }}>
                {/* Requests created by month */}
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Requests Created by Month</div>
                  <div className={s.sectionSub}>Active requests in selected date range</div>
                  {byMonth.length === 0
                    ? <div style={{ color: GREY, fontSize: 13, padding: '24px 0' }}>No data for selected period</div>
                    : (
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={byMonth} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="activeMonthGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor={CRM_BLUE} stopOpacity={0.18} />
                              <stop offset="95%" stopColor={CRM_BLUE} stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} width={45} />
                          <Tooltip
                            content={({ active, payload, label: lbl }: any) => {
                              if (!active || !payload?.length) return null;
                              return (
                                <div style={tooltipStyle}>
                                  <div style={{ fontWeight: 700, marginBottom: 2 }}>{lbl}</div>
                                  <div>{fmtNum(payload[0]?.value)} requests created</div>
                                </div>
                              );
                            }}
                          />
                          <Area type="monotone" dataKey="count" name="Requests created"
                            stroke={CRM_BLUE} strokeWidth={2}
                            fill="url(#activeMonthGrad)" dot={byMonth.length <= 24} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                </div>

                {/* Requests by channel */}
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Requests by Channel</div>
                  <div className={s.sectionSub}>{byChannel.length} channels · selected date range</div>
                  {byChannel.length === 0
                    ? <div style={{ color: GREY, fontSize: 13, padding: '24px 0' }}>No data for selected period</div>
                    : (
                      <ResponsiveContainer width="100%" height={Math.max(180, byChannel.length * 34)}>
                        <BarChart data={byChannel} layout="vertical"
                          margin={{ top: 0, right: 60, bottom: 0, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                          <YAxis type="category" dataKey="channel" width={110}
                            tick={{ fontSize: 11 }} tickFormatter={v => trunc(v, 16)} />
                          <Tooltip
                            content={({ active, payload, label: lbl }: any) => {
                              if (!active || !payload?.length) return null;
                              return (
                                <div style={tooltipStyle}>
                                  <div style={{ fontWeight: 700, marginBottom: 2 }}>{lbl}</div>
                                  <div>{fmtNum(payload[0]?.value)} requests</div>
                                </div>
                              );
                            }}
                          />
                          <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={20}>
                            {byChannel.map((e, i) => (
                              <Cell key={i}
                                fill={CHANNEL_COLOURS[e.channel] ?? BLUE_PALETTE[i % BLUE_PALETTE.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                </div>
              </div>

              {/* Note: ageing not date-filtered */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 7, marginBottom: 12,
                fontSize: 12, color: '#6B7280',
              }}>
                <Info size={14} color="#6B7280" />
                Ageing snapshot shows <strong>all currently open requests</strong> regardless of creation date.
                Other filters (directorate, service type, channel) apply.
              </div>

              {/* Stacked ageing bar by Service Group */}
              {ageing.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Active Requests by Service Group &amp; Days Open</div>
                  <div className={s.sectionSub}>Stacked by ageing bucket — most requests at top</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px', marginBottom: 14 }}>
                    {BUCKET_KEYS.map(b => (
                      <div key={b.key} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                        <div style={{
                          width: 11, height: 11, borderRadius: 2, flexShrink: 0,
                          background: BUCKET_COLOURS[b.label],
                        }} />
                        <span style={{ fontSize: 12, color: '#484848' }}>{b.label}</span>
                      </div>
                    ))}
                  </div>
                  <ResponsiveContainer width="100%" height={Math.max(200, ageing.length * 34)}>
                    <BarChart data={ageing} layout="vertical"
                      margin={{ top: 0, right: 60, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <YAxis type="category" dataKey="serviceGroup" width={200}
                        tick={{ fontSize: 11 }} tickFormatter={v => trunc(v, 28)} />
                      <Tooltip content={<AgeingTooltip />} />
                      {BUCKET_KEYS.map(b => (
                        <Bar key={b.key} dataKey={b.key} name={b.label} stackId="a"
                          fill={BUCKET_COLOURS[b.label]} barSize={22} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Top departments */}
              {byDepartment.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Top Departments by Active Requests</div>
                  <div className={s.sectionSub}>Bars coloured by parent directorate</div>
                  <ResponsiveContainer width="100%" height={Math.max(180, byDepartment.length * 34)}>
                    <BarChart data={byDepartment} layout="vertical"
                      margin={{ top: 0, right: 60, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                      <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <YAxis type="category" dataKey="department" width={200}
                        tick={{ fontSize: 11 }} tickFormatter={v => trunc(v, 28)} />
                      <Tooltip
                        content={({ active, payload, label }: any) => {
                          if (!active || !payload?.length) return null;
                          const row = byDepartment.find(d => d.department === label);
                          return (
                            <div style={{ ...tooltipStyle, padding: '8px 12px' }}>
                              <div style={{ fontWeight: 700, marginBottom: 2 }}>{label}</div>
                              {row && <div style={{ fontSize: 11, color: '#747474', marginBottom: 4 }}>{row.directorate}</div>}
                              <div>{fmtNum(payload[0]?.value)} active</div>
                            </div>
                          );
                        }}
                      />
                      <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={20}>
                        {byDepartment.map((e, i) => (
                          <Cell key={i} fill={dirColour(e.directorate, i)} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Individual request detail table */}
              {activeDetail && activeDetail.length > 0 && (
                <ActiveDetailTable rows={activeDetail} />
              )}
            </>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 3: RESOLVED REQUESTS
           ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'resolved' && !loading && resolvedData && (() => {
          const { kpis, byMonth, byServiceType, dayBuckets, detail: resolvedDetail } = resolvedData;
          const slaPct: number | null = kpis.slaCompliancePct;
          const slaClr = slaColor(slaPct);
          const slaLbl = slaLabel(slaPct);
          return (
            <>
              {/* KPIs */}
              <div className={s.kpiRow3}>
                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Total Resolved</div>
                  <div className={s.kpiValue} style={{ color: CRM_BLUE }}>{fmtNum(kpis.totalResolved)}</div>
                  <div className={s.kpiSub}>closed in selected period</div>
                </div>

                <div className={s.kpiCard}>
                  <div className={s.kpiLabel}>Avg Business Days to Resolve</div>
                  <div className={s.kpiValue}>
                    {kpis.avgBizDays != null ? kpis.avgBizDays.toFixed(1) : '—'}
                  </div>
                  <div className={s.kpiSub}>excluding weekends &amp; Vic public holidays</div>
                </div>

                <div className={s.kpiCard} style={{ borderLeft: `3px solid ${slaClr}` }}>
                  <div className={s.kpiLabel}>SLA Compliance</div>
                  {slaPct !== null ? (
                    <>
                      <div className={s.kpiValue} style={{ color: slaClr }}>
                        {slaPct.toFixed(1)}<span style={{ fontSize: 20, fontWeight: 600, marginLeft: 2 }}>%</span>
                      </div>
                      <div style={{ marginTop: 6, display: 'flex', alignItems: 'center', gap: 5 }}>
                        {slaPct >= 90
                          ? <CheckCircle size={13} color={GREEN} weight="fill" />
                          : slaPct >= 85
                            ? <Warning size={13} color={AMBER} weight="fill" />
                            : <XCircle size={13} color={RED} weight="fill" />}
                        <span style={{ fontSize: 12, color: slaClr, fontWeight: 600 }}>{slaLbl}</span>
                        <span style={{ fontSize: 11, color: '#9CA3AF', marginLeft: 2 }}>Target: 90%</span>
                      </div>
                    </>
                  ) : <div className={s.kpiValue} style={{ color: GREY }}>—</div>}
                </div>
              </div>

              {/* Monthly trend: resolved count + avg biz days */}
              <div className={s.twoCol}>
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Monthly Resolved Count</div>
                  <div className={s.sectionSub}>Filtered by resolution date</div>
                  {byMonth.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No data for this range</div>
                    : (
                      <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={byMonth} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="resolvedGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor={GREEN} stopOpacity={0.2} />
                              <stop offset="95%" stopColor={GREEN} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd"
                            />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                          <Tooltip content={<ChartTooltip />} />
                          <Area type="monotone" dataKey="count" name="Resolved" stroke={GREEN}
                            strokeWidth={2} fill="url(#resolvedGrad)" dot={byMonth.length <= 24} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                </div>

                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Avg Business Days to Resolve by Month</div>
                  <div className={s.sectionSub}>Excluding weekends &amp; Vic public holidays</div>
                  {byMonth.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 160 }}>No data for this range</div>
                    : (
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={byMonth} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd"
                            />
                          <YAxis tick={{ fontSize: 11 }} />
                          <Tooltip
                            formatter={(v: any) => [`${Number(v).toFixed(1)} days`, 'Avg biz days']}
                            labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                          <Line type="monotone" dataKey="avgBizDays" name="Avg biz days"
                            stroke={CRM_BLUE} strokeWidth={2} dot={byMonth.length <= 24} />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                </div>
              </div>

              {/* Day bucket distribution */}
              {dayBuckets.length > 0 && (
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Resolved by Days to Resolve</div>
                  <div className={s.sectionSub}>Business days distribution · filtered period</div>
                  <ResponsiveContainer width="100%" height={180}>
                    <BarChart data={dayBuckets} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                      <XAxis dataKey="label" tick={{ fontSize: 12 }} />
                      <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                      <Tooltip formatter={(v: any) => [fmtNum(v), 'Requests']}
                        labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                      <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={48}>
                        {dayBuckets.map((e, i) => (
                          <Cell key={i} fill={BUCKET_COLOURS[e.label] ?? BLUE_PALETTE[i % BLUE_PALETTE.length]} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Service type table */}
              {byServiceType.length > 0 && (
                <ResolvedServiceTable rows={byServiceType} />
              )}

              {/* Individual request detail table */}
              {resolvedDetail && resolvedDetail.length > 0 && (
                <ResolvedDetailTable rows={resolvedDetail} />
              )}
            </>
          );
        })()}

        {/* ══════════════════════════════════════════════════════════════════════
            Tab 4: COMPLAINTS
           ══════════════════════════════════════════════════════════════════════ */}
        {activeTab === 'complaints' && !loading && complaintsData && (() => {
          const { kpis, byServiceGroup, byMonth, detail } = complaintsData;
          return (
            <>
              {/* Note */}
              <div style={{
                display: 'flex', alignItems: 'center', gap: 7, marginBottom: 14,
                fontSize: 12, color: '#6B7280',
                background: '#F9FAFB', border: '1px solid #E5E7EB',
                borderRadius: 6, padding: '8px 12px',
              }}>
                <Info size={14} color="#6B7280" />
                Cancelled requests and sensitive requests (no SLA state) are excluded from all complaint figures.
              </div>

              {/* KPI */}
              <div style={{ marginBottom: 24 }}>
                <div className={s.kpiCard} style={{ maxWidth: 240 }}>
                  <div className={s.kpiLabel}>Total Complaints</div>
                  <div className={s.kpiValue} style={{ color: kpis.totalComplaints > 0 ? RED : GREEN }}>
                    {fmtNum(kpis.totalComplaints)}
                  </div>
                  <div className={s.kpiSub}>in selected period</div>
                </div>
              </div>

              <div className={s.twoCol}>
                {/* By service group */}
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Complaints by Service Group</div>
                  <div className={s.sectionSub}>Filtered period</div>
                  {byServiceGroup.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 140 }}>No complaints in this period</div>
                    : (
                      <ResponsiveContainer width="100%" height={Math.max(160, byServiceGroup.length * 34)}>
                        <BarChart data={byServiceGroup} layout="vertical"
                          margin={{ top: 0, right: 40, bottom: 0, left: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#F3F4F6" />
                          <XAxis type="number" tick={{ fontSize: 11 }} tickFormatter={fmtNum} />
                          <YAxis type="category" dataKey="serviceGroup" width={180}
                            tick={{ fontSize: 11 }} tickFormatter={v => trunc(v, 26)} />
                          <Tooltip formatter={(v: any) => [fmtNum(v), 'Complaints']}
                            labelStyle={{ fontWeight: 600 }} contentStyle={tooltipStyle} />
                          <Bar dataKey="count" radius={[0, 4, 4, 0]} barSize={20}>
                            {byServiceGroup.map((_, i) => (
                              <Cell key={i} fill={BLUE_PALETTE[i % BLUE_PALETTE.length]} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    )}
                </div>

                {/* Monthly trend */}
                <div className={s.sectionCard}>
                  <div className={s.sectionTitle}>Monthly Complaint Trend</div>
                  <div className={s.sectionSub}>Filtered by creation date</div>
                  {byMonth.length === 0
                    ? <div className={s.centeredMsg} style={{ minHeight: 140 }}>No trend data</div>
                    : (
                      <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={byMonth} margin={{ top: 4, right: 16, bottom: 0, left: 0 }}>
                          <defs>
                            <linearGradient id="compGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%"  stopColor={RED} stopOpacity={0.18} />
                              <stop offset="95%" stopColor={RED} stopOpacity={0} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
                          <XAxis dataKey="label" tick={{ fontSize: 10 }} interval="preserveStartEnd"
                            />
                          <YAxis tick={{ fontSize: 11 }} tickFormatter={fmtNum} allowDecimals={false} />
                          <Tooltip content={<ChartTooltip />} />
                          <Area type="monotone" dataKey="count" name="Complaints" stroke={RED}
                            strokeWidth={2} fill="url(#compGrad)" dot={byMonth.length <= 24} />
                        </AreaChart>
                      </ResponsiveContainer>
                    )}
                </div>
              </div>

              {/* Detail table */}
              {detail.length > 0 && (
                <ComplaintsDetailTable rows={detail} />
              )}
            </>
          );
        })()}

        {/* Loading overlay (refreshing while data visible) */}
        {loading && (overviewData || activeData || resolvedData || complaintsData) && (
          <div style={{
            position: 'fixed', inset: 0, background: 'rgba(255,255,255,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999,
          }}>
            <Spinner size={SpinnerSize.large} label="Refreshing…" />
          </div>
        )}

      </div>
    </div>
  );
}

// ── Shared detail table helpers ───────────────────────────────────────────────

const TABLE_PAGE_SIZE = 50;

const tdBase: React.CSSProperties = {
  padding: '7px 10px', borderBottom: '1px solid #F3F4F6',
  fontSize: 12, verticalAlign: 'top',
};
const thBase: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontWeight: 700,
  fontSize: 11, color: '#747474', background: '#F9FAFB',
  borderBottom: '1px solid #E1E1E1', whiteSpace: 'nowrap',
};

function TableSearchBar({
  search, onSearch, filters, onClear, totalFiltered, totalAll,
}: {
  search: string;
  onSearch: (v: string) => void;
  filters: React.ReactNode;
  onClear: () => void;
  totalFiltered: number;
  totalAll: number;
}) {
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 14, alignItems: 'center' }}>
      <input
        type="text"
        placeholder="Search ref, subject, department…"
        value={search}
        onChange={e => onSearch(e.target.value)}
        style={{
          padding: '6px 10px', border: '1px solid #E1E1E1', borderRadius: 6,
          fontSize: 13, minWidth: 220, outline: 'none',
        }}
      />
      {filters}
      {(search || totalFiltered < totalAll) && (
        <button onClick={onClear}
          style={{ padding: '6px 10px', background: 'none', border: '1px solid #E1E1E1', borderRadius: 6, fontSize: 12, cursor: 'pointer', color: '#747474' }}>
          Clear
        </button>
      )}
      <span style={{ marginLeft: 'auto', fontSize: 12, color: '#9CA3AF' }}>
        {fmtNum(totalFiltered)}{totalFiltered < totalAll ? ` of ${fmtNum(totalAll)}` : ''} rows
      </span>
    </div>
  );
}

function Paginator({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (p: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 8, marginTop: 14 }}>
      <button onClick={() => onChange(0)} disabled={page === 0}
        style={{ padding: '4px 8px', border: '1px solid #E1E1E1', borderRadius: 5, cursor: page === 0 ? 'default' : 'pointer', background: '#fff', color: page === 0 ? '#D1D5DB' : '#484848', fontSize: 12 }}>
        «
      </button>
      <button onClick={() => onChange(page - 1)} disabled={page === 0}
        style={{ padding: '4px 10px', border: '1px solid #E1E1E1', borderRadius: 5, cursor: page === 0 ? 'default' : 'pointer', background: '#fff', color: page === 0 ? '#D1D5DB' : '#484848', fontSize: 12 }}>
        ← Prev
      </button>
      <span style={{ fontSize: 12, color: '#747474', minWidth: 120, textAlign: 'center' }}>
        Page {page + 1} of {totalPages}
      </span>
      <button onClick={() => onChange(page + 1)} disabled={page >= totalPages - 1}
        style={{ padding: '4px 10px', border: '1px solid #E1E1E1', borderRadius: 5, cursor: page >= totalPages - 1 ? 'default' : 'pointer', background: '#fff', color: page >= totalPages - 1 ? '#D1D5DB' : '#484848', fontSize: 12 }}>
        Next →
      </button>
      <button onClick={() => onChange(totalPages - 1)} disabled={page >= totalPages - 1}
        style={{ padding: '4px 8px', border: '1px solid #E1E1E1', borderRadius: 5, cursor: page >= totalPages - 1 ? 'default' : 'pointer', background: '#fff', color: page >= totalPages - 1 ? '#D1D5DB' : '#484848', fontSize: 12 }}>
        »
      </button>
    </div>
  );
}

function SelectFilter({ value, onChange, options, placeholder }: {
  value: string; onChange: (v: string) => void;
  options: string[]; placeholder: string;
}) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ padding: '6px 10px', border: '1px solid #E1E1E1', borderRadius: 6, fontSize: 13, color: value ? '#232323' : '#9CA3AF' }}>
      <option value="">{placeholder}</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function daysOpenColour(d: number): string {
  if (d <= 5)  return GREEN;
  if (d <= 10) return '#16a34a';
  if (d <= 20) return AMBER;
  if (d <= 50) return '#f97316';
  return RED;
}

function SlaBadge({ status }: { status: string }) {
  const bg   = status === 'Compliant' ? '#F0F7F1' : status === 'Breach' || status === 'Overdue' ? '#FEF2F2' : '#F3F4F6';
  const clr  = status === 'Compliant' ? GREEN : status === 'Breach' || status === 'Overdue' ? RED : GREY;
  return (
    <span style={{ padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: bg, color: clr }}>
      {status}
    </span>
  );
}

// ── Active Requests Detail Table ──────────────────────────────────────────────

function ActiveDetailTable({ rows }: { rows: ActiveDetailRow[] }) {
  const [search,    setSearch]    = useState('');
  const [svcFilter, setSvcFilter] = useState('');
  const [slaFilter, setSlaFilter] = useState('');
  const [page,      setPage]      = useState(0);

  // Reset page when filters change
  React.useEffect(() => { setPage(0); }, [search, svcFilter, slaFilter]);

  const serviceGroups = React.useMemo(() => [...new Set(rows.map(r => r.serviceGroup))].sort(), [rows]);

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(row => {
      const matchQ   = !q || (row.refNumber || '').toLowerCase().includes(q)
                          || (row.subject || '').toLowerCase().includes(q)
                          || (row.department || '').toLowerCase().includes(q)
                          || (row.serviceGroup || '').toLowerCase().includes(q);
      const matchSvc = !svcFilter || row.serviceGroup === svcFilter;
      const matchSla = !slaFilter || row.slaStatus === slaFilter;
      return matchQ && matchSvc && matchSla;
    });
  }, [rows, search, svcFilter, slaFilter]);

  const totalPages = Math.ceil(filtered.length / TABLE_PAGE_SIZE);
  const pageRows   = filtered.slice(page * TABLE_PAGE_SIZE, (page + 1) * TABLE_PAGE_SIZE);

  const handleClear = () => { setSearch(''); setSvcFilter(''); setSlaFilter(''); };

  return (
    <div style={{ background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Active Request Detail</div>
      <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 14 }}>
        Showing {fmtNum(rows.length)} longest-open requests · use filters to narrow down
      </div>
      <TableSearchBar
        search={search} onSearch={setSearch}
        totalFiltered={filtered.length} totalAll={rows.length}
        onClear={handleClear}
        filters={<>
          <SelectFilter value={svcFilter} onChange={setSvcFilter} options={serviceGroups} placeholder="All service groups" />
          <SelectFilter value={slaFilter} onChange={setSlaFilter} options={['Overdue', 'Active']} placeholder="All SLA status" />
        </>}
      />
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Ref', 'Subject', 'Service Group', 'Type L1', 'Department', 'Days Open', 'Channel', 'SLA'].map(h => (
                <th key={h} style={thBase}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={8} style={{ ...tdBase, textAlign: 'center', color: '#9CA3AF', padding: 24 }}>No matching requests</td></tr>
            ) : pageRows.map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                <td style={{ ...tdBase, fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap' }}>{row.refNumber || '—'}</td>
                <td style={{ ...tdBase, maxWidth: 220 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.subject}>
                    {row.subject || '—'}
                  </div>
                </td>
                <td style={tdBase}>{row.serviceGroup}</td>
                <td style={{ ...tdBase, color: '#6B7280' }}>{row.serviceTypeL1}</td>
                <td style={tdBase}>
                  {row.department}
                  {row.directorate && row.directorate !== 'Unassigned' && (
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>{row.directorate}</div>
                  )}
                </td>
                <td style={{ ...tdBase, fontWeight: 700, color: daysOpenColour(row.daysOpen), whiteSpace: 'nowrap' }}>
                  {row.daysOpen}d
                </td>
                <td style={{ ...tdBase, color: '#6B7280', whiteSpace: 'nowrap' }}>{row.channel}</td>
                <td style={tdBase}><SlaBadge status={row.slaStatus} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paginator page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

// ── Resolved Requests Detail Table ────────────────────────────────────────────

function ResolvedDetailTable({ rows }: { rows: ResolvedDetailRow[] }) {
  const [search,    setSearch]    = useState('');
  const [svcFilter, setSvcFilter] = useState('');
  const [slaFilter, setSlaFilter] = useState('');
  const [page,      setPage]      = useState(0);

  React.useEffect(() => { setPage(0); }, [search, svcFilter, slaFilter]);

  const serviceGroups = React.useMemo(() => [...new Set(rows.map(r => r.serviceGroup))].sort(), [rows]);

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(row => {
      const matchQ   = !q || (row.refNumber || '').toLowerCase().includes(q)
                          || (row.subject || '').toLowerCase().includes(q)
                          || (row.department || '').toLowerCase().includes(q)
                          || (row.serviceGroup || '').toLowerCase().includes(q);
      const matchSvc = !svcFilter || row.serviceGroup === svcFilter;
      const matchSla = !slaFilter || row.slaStatus === slaFilter;
      return matchQ && matchSvc && matchSla;
    });
  }, [rows, search, svcFilter, slaFilter]);

  const totalPages = Math.ceil(filtered.length / TABLE_PAGE_SIZE);
  const pageRows   = filtered.slice(page * TABLE_PAGE_SIZE, (page + 1) * TABLE_PAGE_SIZE);

  const handleClear = () => { setSearch(''); setSvcFilter(''); setSlaFilter(''); };

  return (
    <div style={{ background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Resolved Request Detail</div>
      <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 14 }}>
        {fmtNum(rows.length)} most recently resolved in selected period · use filters to narrow down
      </div>
      <TableSearchBar
        search={search} onSearch={setSearch}
        totalFiltered={filtered.length} totalAll={rows.length}
        onClear={handleClear}
        filters={<>
          <SelectFilter value={svcFilter} onChange={setSvcFilter} options={serviceGroups} placeholder="All service groups" />
          <SelectFilter value={slaFilter} onChange={setSlaFilter} options={['Compliant', 'Breach', 'N/A']} placeholder="All SLA status" />
        </>}
      />
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Ref', 'Subject', 'Service Group', 'Type L1', 'Department', 'Created', 'Resolved', 'Biz Days', 'SLA'].map(h => (
                <th key={h} style={thBase}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={9} style={{ ...tdBase, textAlign: 'center', color: '#9CA3AF', padding: 24 }}>No matching requests</td></tr>
            ) : pageRows.map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                <td style={{ ...tdBase, fontFamily: 'monospace', fontSize: 11, whiteSpace: 'nowrap' }}>{row.refNumber || '—'}</td>
                <td style={{ ...tdBase, maxWidth: 220 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.subject}>
                    {row.subject || '—'}
                  </div>
                </td>
                <td style={tdBase}>{row.serviceGroup}</td>
                <td style={{ ...tdBase, color: '#6B7280' }}>{row.serviceTypeL1}</td>
                <td style={tdBase}>
                  {row.department}
                  {row.directorate && row.directorate !== 'Unassigned' && (
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>{row.directorate}</div>
                  )}
                </td>
                <td style={{ ...tdBase, whiteSpace: 'nowrap', color: '#6B7280' }}>{row.createdDate}</td>
                <td style={{ ...tdBase, whiteSpace: 'nowrap' }}>{row.closedDate}</td>
                <td style={{ ...tdBase, fontWeight: 700, color: daysOpenColour(row.bizDaysToResolve) }}>
                  {row.bizDaysToResolve}d
                </td>
                <td style={tdBase}><SlaBadge status={row.slaStatus} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paginator page={page} totalPages={totalPages} onChange={setPage} />
    </div>
  );
}

// ── Complaint Request Detail Panel ───────────────────────────────────────────

function ComplaintDetailPanel({ row, onClose }: { row: ComplaintRow; onClose: () => void }) {
  // Close on Escape
  React.useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  function Field({ label, children }: { label: string; children: React.ReactNode }) {
    return (
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 4 }}>
          {label}
        </div>
        <div style={{ fontSize: 13, color: '#232323' }}>{children}</div>
      </div>
    );
  }

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.32)', zIndex: 1000 }}
      />
      {/* Slide-out panel */}
      <div style={{
        position: 'fixed', right: 0, top: 0, bottom: 0, width: 460,
        background: '#fff', zIndex: 1001,
        display: 'flex', flexDirection: 'column',
        boxShadow: '-6px 0 32px rgba(0,0,0,0.14)',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 24px 16px', borderBottom: '1px solid #E1E1E1',
          display: 'flex', alignItems: 'flex-start', gap: 12,
        }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontFamily: 'monospace', fontSize: 12, color: '#9CA3AF', marginBottom: 6 }}>
              {row.refNumber || '—'}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#1A1A1A', lineHeight: '1.4', wordBreak: 'break-word' }}>
              {row.subject || 'No subject'}
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: '1px solid #E1E1E1', borderRadius: 6,
              cursor: 'pointer', color: '#747474', padding: '4px 6px',
              display: 'flex', alignItems: 'center', flexShrink: 0,
              transition: 'all 0.15s',
            }}
          >
            <PhosphorX size={16} weight="bold" />
          </button>
        </div>

        {/* Status badges */}
        <div style={{ padding: '12px 24px', borderBottom: '1px solid #F3F4F6', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <span style={{
            padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600,
            background: row.status === 'Closed' ? '#F0F7F1' : '#FFF7ED',
            color: row.status === 'Closed' ? GREEN : AMBER,
          }}>{row.status || '—'}</span>
          <SlaBadge status={row.slaStatus} />
          <span style={{
            padding: '2px 10px', borderRadius: 10, fontSize: 12, fontWeight: 600,
            background: '#FEF2F2', color: RED,
          }}>Complaint</span>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '20px 24px' }}>
          {/* Service */}
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Service</div>
            <Field label="Service Group">{row.serviceGroup || '—'}</Field>
            <Field label="Service Type L1">{row.serviceTypeL1 || '—'}</Field>
            {row.serviceTypeL2 && row.serviceTypeL2 !== 'Other' && (
              <Field label="Service Type L2">{row.serviceTypeL2}</Field>
            )}
          </div>

          {/* Organisation */}
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Organisation</div>
            <Field label="Department">{row.department || '—'}</Field>
            <Field label="Directorate">{row.directorate || '—'}</Field>
          </div>

          {/* Dates */}
          <div style={{ marginBottom: 20, paddingBottom: 20, borderBottom: '1px solid #F3F4F6' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>Dates</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <Field label="Created">{row.createdDate || '—'}</Field>
              <Field label="Closed">{row.closedDate || '—'}</Field>
            </div>
          </div>

          {/* SLA */}
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 12 }}>SLA</div>
            <Field label="SLA Status"><SlaBadge status={row.slaStatus} /></Field>
          </div>
        </div>
      </div>
    </>
  );
}

// ── Complaints Detail Table ───────────────────────────────────────────────────

type ComplaintRow = ComplaintsData['detail'][0];

function ComplaintsDetailTable({ rows }: { rows: ComplaintRow[] }) {
  const [search,       setSearch]       = useState('');
  const [svcFilter,    setSvcFilter]    = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [slaFilter,    setSlaFilter]    = useState('');
  const [page,         setPage]         = useState(0);
  const [selectedRow,  setSelectedRow]  = useState<ComplaintRow | null>(null);

  React.useEffect(() => { setPage(0); }, [search, svcFilter, statusFilter, slaFilter]);

  const serviceGroups = React.useMemo(() => [...new Set(rows.map(r => r.serviceGroup))].sort(), [rows]);
  const statuses      = React.useMemo(() => [...new Set(rows.map(r => r.status).filter(Boolean))].sort(), [rows]);

  const filtered = React.useMemo(() => {
    const q = search.toLowerCase();
    return rows.filter(row => {
      const matchQ      = !q || (row.refNumber || '').toLowerCase().includes(q)
                              || (row.subject || '').toLowerCase().includes(q)
                              || (row.department || '').toLowerCase().includes(q)
                              || (row.serviceGroup || '').toLowerCase().includes(q);
      const matchSvc    = !svcFilter    || row.serviceGroup === svcFilter;
      const matchStatus = !statusFilter || row.status === statusFilter;
      const matchSla    = !slaFilter    || row.slaStatus === slaFilter;
      return matchQ && matchSvc && matchStatus && matchSla;
    });
  }, [rows, search, svcFilter, statusFilter, slaFilter]);

  const totalPages = Math.ceil(filtered.length / TABLE_PAGE_SIZE);
  const pageRows   = filtered.slice(page * TABLE_PAGE_SIZE, (page + 1) * TABLE_PAGE_SIZE);

  const handleClear = () => { setSearch(''); setSvcFilter(''); setStatusFilter(''); setSlaFilter(''); };

  return (
    <>
    {selectedRow && (
      <ComplaintDetailPanel row={selectedRow} onClose={() => setSelectedRow(null)} />
    )}
    <div style={{ background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10, padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Complaint Detail</div>
      <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 14 }}>
        {fmtNum(rows.length)} complaints in selected period · click a Ref to view details
      </div>
      <TableSearchBar
        search={search} onSearch={setSearch}
        totalFiltered={filtered.length} totalAll={rows.length}
        onClear={handleClear}
        filters={<>
          <SelectFilter value={svcFilter}    onChange={setSvcFilter}    options={serviceGroups} placeholder="All service groups" />
          <SelectFilter value={statusFilter} onChange={setStatusFilter} options={statuses}      placeholder="All statuses" />
          <SelectFilter value={slaFilter}    onChange={setSlaFilter}    options={['Compliant', 'Breach', 'N/A']} placeholder="All SLA status" />
        </>}
      />
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              {['Ref', 'Subject', 'Service Group', 'Service Type', 'Department', 'Created', 'Status', 'SLA'].map(h => (
                <th key={h} style={thBase}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pageRows.length === 0 ? (
              <tr><td colSpan={8} style={{ ...tdBase, textAlign: 'center', color: '#9CA3AF', padding: 24 }}>No matching complaints</td></tr>
            ) : pageRows.map((row, i) => (
              <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                <td style={{ ...tdBase, whiteSpace: 'nowrap' }}>
                  <button
                    onClick={() => setSelectedRow(row)}
                    style={{
                      background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                      fontFamily: 'monospace', fontSize: 11,
                      color: CRM_BLUE, textDecoration: 'underline', fontWeight: 600,
                    }}
                  >
                    {row.refNumber || '—'}
                  </button>
                </td>
                <td style={{ ...tdBase, maxWidth: 220 }}>
                  <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.subject}>
                    {row.subject || '—'}
                  </div>
                </td>
                <td style={tdBase}>{row.serviceGroup}</td>
                <td style={{ ...tdBase, color: '#6B7280' }}>
                  {row.serviceTypeL1}
                  {row.serviceTypeL2 && row.serviceTypeL2 !== 'Other' && (
                    <span style={{ color: '#9CA3AF' }}> › {row.serviceTypeL2}</span>
                  )}
                </td>
                <td style={tdBase}>
                  {row.department}
                  {row.directorate && row.directorate !== 'Unassigned' && (
                    <div style={{ fontSize: 11, color: '#9CA3AF' }}>{row.directorate}</div>
                  )}
                </td>
                <td style={{ ...tdBase, whiteSpace: 'nowrap' }}>{row.createdDate}</td>
                <td style={tdBase}>
                  <span style={{
                    padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 600,
                    background: row.status === 'Closed' ? '#F0F7F1' : '#FFF7ED',
                    color: row.status === 'Closed' ? GREEN : AMBER,
                  }}>{row.status}</span>
                </td>
                <td style={tdBase}><SlaBadge status={row.slaStatus} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Paginator page={page} totalPages={totalPages} onChange={setPage} />
    </div>
    </>
  );
}

// ── Resolved Service Type Table (sortable) ─────────────────────────────────────
type ResolvedRow = ResolvedData['byServiceType'][0];
type SortKey = 'count' | 'avgBizDays' | 'minBizDays' | 'maxBizDays' | 'slaCompliancePct' | 'serviceGroup' | 'serviceTypeL1' | 'serviceTypeL2';

function ResolvedServiceTable({ rows }: { rows: ResolvedRow[] }) {
  const [sortKey, setSortKey]   = useState<SortKey>('count');
  const [sortAsc, setSortAsc]   = useState(false);

  const sorted = [...rows].sort((a, b) => {
    const va = a[sortKey] ?? -Infinity;
    const vb = b[sortKey] ?? -Infinity;
    return sortAsc
      ? String(va).localeCompare(String(vb), undefined, { numeric: true })
      : String(vb).localeCompare(String(va), undefined, { numeric: true });
  });

  function th(label: string, key: SortKey) {
    const active = sortKey === key;
    return (
      <th
        style={{
          textAlign: 'left', padding: '8px 10px', fontWeight: 700, fontSize: 11,
          color: active ? CRM_BLUE : '#747474', background: '#F9FAFB',
          borderBottom: '1px solid #E1E1E1', cursor: 'pointer',
          userSelect: 'none', whiteSpace: 'nowrap',
        }}
        onClick={() => {
          if (sortKey === key) setSortAsc(a => !a);
          else { setSortKey(key); setSortAsc(false); }
        }}
      >
        {label} {active ? (sortAsc ? '↑' : '↓') : ''}
      </th>
    );
  }

  const td: React.CSSProperties = {
    padding: '7px 10px', borderBottom: '1px solid #F3F4F6', fontSize: 12, verticalAlign: 'top',
  };

  return (
    <div style={{
      background: '#fff', border: '1px solid #E1E1E1', borderRadius: 10,
      padding: '18px 20px', boxShadow: '0 1px 4px rgba(0,0,0,0.04)', marginBottom: 16,
    }}>
      <div style={{ fontSize: 14, fontWeight: 700, marginBottom: 2 }}>Resolution by Service Type</div>
      <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 14 }}>
        Average business days · click column headers to sort
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              {th('Service Group', 'serviceGroup')}
              {th('Type L1', 'serviceTypeL1')}
              {th('Type L2', 'serviceTypeL2')}
              {th('Resolved', 'count')}
              {th('Avg Days', 'avgBizDays')}
              {th('Min', 'minBizDays')}
              {th('Max', 'maxBizDays')}
              {th('SLA %', 'slaCompliancePct')}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row, i) => {
              const slaPct = row.slaCompliancePct;
              const slaClr = slaColor(slaPct);
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#FAFAFA' }}>
                  <td style={td}>{row.serviceGroup}</td>
                  <td style={{ ...td, color: '#6B7280' }}>{row.serviceTypeL1}</td>
                  <td style={{ ...td, color: '#9CA3AF' }}>{row.serviceTypeL2}</td>
                  <td style={{ ...td, fontVariantNumeric: 'tabular-nums' }}>{fmtNum(row.count)}</td>
                  <td style={{ ...td, fontWeight: 600 }}>
                    {row.avgBizDays != null ? row.avgBizDays.toFixed(1) : '—'}
                  </td>
                  <td style={td}>{row.minBizDays}</td>
                  <td style={td}>{row.maxBizDays}</td>
                  <td style={td}>
                    {slaPct != null ? (
                      <span style={{
                        padding: '1px 7px', borderRadius: 10, fontSize: 11, fontWeight: 700,
                        background: slaPct >= 90 ? '#F0F7F1' : slaPct >= 85 ? '#FFFBEB' : '#FEF2F2',
                        color: slaClr,
                      }}>
                        {slaPct.toFixed(1)}%
                      </span>
                    ) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
